import { useEffect, useState } from 'react';
import type { HardwareCodecProbeResult, SoftwareEncodeRecoveryState, StreamDebugOverlaySnapshot } from '@playcastdotio/PeerCore';
import { peerCoordinator } from '../../../coordinators/init';
import { buildHostStreamDebugSnapshot, StreamDebugOverlay } from './StreamDebugOverlay';
import { HardwareCodecWarningBanner } from './HardwareCodecWarningBanner';

type PeerCodecStatus = {
  result: HardwareCodecProbeResult;
  softwareEncodeRecoveryState?: SoftwareEncodeRecoveryState;
};

const EMPTY_SNAPSHOT: StreamDebugOverlaySnapshot = {
  codecKind: 'unknown',
  framesPerSecond: 0,
  frameWidth: 0,
  frameHeight: 0,
};

const STATS_POLL_MS = 1000;

export { buildHostStreamDebugSnapshot };

/**
 * Host Beacon debug overlay — always visible on Beacon (developer machine).
 * Encoder, warning, outbound stats and guest diagnostics all describe the same peer.
 */
export function StreamDebugOverlayHost() {
  const [{ snapshot, recoveryState }, setView] = useState<{
    snapshot: StreamDebugOverlaySnapshot;
    recoveryState?: SoftwareEncodeRecoveryState;
  }>({ snapshot: EMPTY_SNAPSHOT });

  useEffect(() => {
    const codecs = new Map<string, PeerCodecStatus>();
    let previousPeerIds = new Set<string>();

    const refreshFromStats = () => {
      const allStats = peerCoordinator?.getAllPeerStats?.() ?? [];
      const activePeerIds = new Set(allStats.map((stats) => stats.peerId));
      for (const peerId of previousPeerIds) {
        if (!activePeerIds.has(peerId)) codecs.delete(peerId);
      }
      previousPeerIds = activePeerIds;
      const selected = allStats.reduce<(typeof allStats)[number] | undefined>(
        (best, stats) => !best || (stats.framesPerSecond ?? 0) >= (best.framesPerSecond ?? 0) ? stats : best,
        undefined,
      );
      const status = selected ? codecs.get(selected.peerId) : undefined;
      const result = status?.result;
      setView({
        snapshot: buildHostStreamDebugSnapshot(
          result?.encode ?? 'unknown',
          selected ? [selected] : [],
          result?.encoderImplementation,
          result?.negotiatedVideoCodec,
          result?.offeredVideoProfiles,
          result?.sendCodecPin,
          (peerId) => peerCoordinator?.getGuestLinkDebugSnapshot?.(peerId),
        ),
        recoveryState: status?.softwareEncodeRecoveryState,
      });
    };

    const unsubHardware = peerCoordinator?.hardwareCodecStatus?.addListener(
      ({ peerId, result, softwareEncodeRecoveryState }) => {
        codecs.set(peerId, { result, softwareEncodeRecoveryState });
        refreshFromStats();
      },
    );

    const unsubDisconnected = peerCoordinator?.events?.allPeersDisconnected?.addListener(() => {
      codecs.clear();
      previousPeerIds.clear();
      setView({ snapshot: EMPTY_SNAPSHOT });
    });

    const removePeer = ({ peerId }: { peerId: string }) => {
      codecs.delete(peerId);
      refreshFromStats();
    };
    const unsubPlayerLeft = peerCoordinator?.events?.onPlayerLeft?.addListener(removePeer);
    const unsubAdminLeft = peerCoordinator?.events?.onAdminLeft?.addListener(removePeer);

    refreshFromStats();
    const interval = setInterval(refreshFromStats, STATS_POLL_MS);

    return () => {
      unsubHardware?.();
      unsubDisconnected?.();
      unsubPlayerLeft?.();
      unsubAdminLeft?.();
      clearInterval(interval);
    };
  }, []);

  return <>
    <StreamDebugOverlay snapshot={snapshot} />
    <HardwareCodecWarningBanner
      encodeKind={recoveryState === 'refused' ? snapshot.codecKind : 'unknown'}
      encoderImplementation={snapshot.implementation}
    />
  </>;
}
